/**
 * Follow-ups list export — CSV + print-friendly HTML for Overdue Recall and
 * Unscheduled Treatment (no backend call, pure util, node-testable).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE so node-tests execute it verbatim.
 * The only exception is rowModel.js which is itself import-free. If rowModel
 * ever gains heavy deps, replace the two calls below with the local formatters
 * defined at the bottom of this file (they are identical in logic).
 *
 * PHI masking: rows arrive already masked at the data boundary — never mask here.
 *
 * Run standalone:  node src/utils/followups/listExport.js   (should print nothing)
 */

// formatDate and formatMoney are inlined from rowModel.js (lines 40/58) to keep
// this file import-free so node-tests execute it verbatim (same convention as
// phiMask.js and rowModel.js itself). Keep in sync with rowModel.js.

const _YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;
function _ymdOf(value) {
  if (typeof value !== 'string') return null;
  const m = _YMD_RE.exec(value);
  if (!m) return null;
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function formatDate(value, format) {
  const ymd = _ymdOf(value);
  if (!ymd) return '';
  const fmt = format || 'MM/DD/YYYY';
  const y = ymd.slice(0, 4);
  const m = ymd.slice(5, 7);
  const d = ymd.slice(8, 10);
  return fmt.replace(/YYYY|YY|MM|M|DD|D/g, (t) => {
    if (t === 'YYYY') return y;
    if (t === 'YY') return y.slice(2);
    if (t === 'MM') return m;
    if (t === 'M') return String(Number(m));
    if (t === 'DD') return d;
    return String(Number(d));
  });
}

function formatMoney(value, symbol) {
  const sym = symbol || '$';
  if (value === null || value === undefined || value === '') return '';
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '';
  const body = Math.abs(n).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  return (n < 0 ? '-' : '') + sym + body;
}

// ── csvEscape ─────────────────────────────────────────────────────────────────
// RFC-4180 escaping. Inlined (not imported from paymentsCsv.js) so the file
// stays self-contained for node-test runs that execute only this file.
// Keep in sync with src/utils/paymentsCsv.js::csvEscape.
function csvEscape(value) {
  const str = value === null || value === undefined ? '' : String(value);
  if (str.indexOf(',') === -1 && str.indexOf('"') === -1 && str.indexOf('\n') === -1 && str.indexOf('\r') === -1) {
    return str;
  }
  return '"' + str.replace(/"/g, '""') + '"';
}

// ── EXPORT_COLUMNS ────────────────────────────────────────────────────────────
// SOURCE: AdminTable.vue cell slots (lines 295-464) and each page's `columns`.
// AGREEMENT: the headers here must match the column definitions in
//   OverdueRecall.vue    — the `columns` computed
//   UnscheduledTreatment.vue — the `columns` computed
// If you rename a column header there, rename it here too (and vice versa).

export const EXPORT_COLUMNS = {
  // SOURCE: OverdueRecall.vue `columns` + AdminTable.vue slots
  overdueRecall: [
    { header: 'Patient',           field: 'Patient',                      kind: 'text' },
    { header: 'Last Hygienist',    field: 'LastHygienist',                kind: 'text' },
    { header: 'Due Date',          field: 'DateDue',                      kind: 'date' },
    { header: 'Recall Type',       field: 'RecallType',                   kind: 'text' },
    { header: 'Recall Interval',   field: 'RecallInterval',               kind: 'text' },
    { header: 'Carrier',           field: 'Carrier',                      kind: 'text' },
    { header: 'Freq 2x/yr',        field: 'PreventiveFrequency2xPerYear', kind: 'text' },
    // Auto Reminders: included only when ctx.remindersPaidFor = true
    { header: 'Auto Reminders',    field: 'AutoRemindersSent',            kind: 'text', conditional: 'remindersPaidFor' },
    { header: 'Status',            field: 'Status',                       kind: 'status' },
    { header: 'Attempts',          field: 'Attempts',                     kind: 'text' },
  ],

  // SOURCE: UnscheduledTreatment.vue `columns` + AdminTable.vue slots
  unscheduledTreatment: [
    { header: 'Patient',           field: 'Patient',          kind: 'text' },
    { header: 'Date Tx Planned',   field: 'dateTxPlanned',    kind: 'date' },
    { header: 'Carrier',           field: 'Carrier',          kind: 'text' },
    { header: 'Annual Max',        field: 'annualMax',         kind: 'money' },
    { header: 'Used',              field: 'amountUsed',        kind: 'money' },
    { header: 'Remaining',         field: 'amountRemaining',   kind: 'money' },
    { header: 'Tx Plan Amount',    field: 'officeFeeTotal',    kind: 'money' },
    { header: 'Adjusted',          field: 'adjustedFeeTotal',  kind: 'money' },
    { header: 'Status',            field: 'Status',            kind: 'status' },
    { header: 'Attempts',          field: 'Attempts',          kind: 'text' },
  ],
};

// ── buildExportMatrix ─────────────────────────────────────────────────────────

/**
 * Turn raw queue rows into a matrix of plain strings, ready for CSV or HTML.
 *
 * @param {string} queue  'overdueRecall' | 'unscheduledTreatment'
 * @param {Array}  rows   the page-level computed array (already PHI-masked if applicable)
 * @param {Object} ctx    { dateFormatStr, currencySymbol, remindersPaidFor }
 * @returns {{ headers: string[], rows: string[][] }}
 */
export function buildExportMatrix(queue, rows, ctx) {
  const c = ctx || {};
  const dateFormatStr = c.dateFormatStr || 'MM/DD/YYYY';
  const currencySymbol = c.currencySymbol || '$';
  const remindersPaidFor = !!c.remindersPaidFor;

  const colDefs = (EXPORT_COLUMNS[queue] || []).filter((col) => {
    if (col.conditional === 'remindersPaidFor') return remindersPaidFor;
    return true;
  });

  const headers = colDefs.map(c => c.header);

  const dataRows = (Array.isArray(rows) ? rows : []).map((row) => {
    const r = row || {};
    return colDefs.map((col) => {
      const raw = r[col.field];
      if (raw === null || raw === undefined) return '';

      if (col.kind === 'date') {
        return formatDate(raw, dateFormatStr);
      }
      if (col.kind === 'money') {
        return formatMoney(raw, currencySymbol);
      }
      if (col.kind === 'status' && queue === 'unscheduledTreatment') {
        // Mirror of AdminTable.vue's Status slot for unscheduled treatment:
        // NextVisit !== null → "Apt on Schedule <formatted date>"
        if (r.NextVisit !== null && r.NextVisit !== undefined) {
          const fmtDate = formatDate(r.NextVisit, dateFormatStr);
          return 'Apt on Schedule' + (fmtDate ? ' ' + fmtDate : '');
        }
        return raw === null || raw === undefined ? '' : String(raw);
      }
      return String(raw);
    });
  });

  return { headers, rows: dataRows };
}

// ── toCsv ─────────────────────────────────────────────────────────────────────

/**
 * Build a UTF-8-BOM CSV string from a matrix.
 * Uses \r\n line endings (RFC-4180 + Excel compatible).
 *
 * @param {string[]} headers
 * @param {string[][]} rows
 * @returns {string}
 */
export function toCsv(headers, rows) {
  const lines = [];
  lines.push(headers.map(csvEscape).join(','));
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    lines.push((Array.isArray(row) ? row : []).map(csvEscape).join(','));
  });
  return '﻿' + lines.join('\r\n');
}

// ── buildPrintHtml ────────────────────────────────────────────────────────────

/**
 * Build a self-contained print-friendly HTML document string.
 *
 * @param {string}   title   e.g. 'Overdue Recall'
 * @param {string}   meta    date-range + generated-on line
 * @param {string[]} headers column headers
 * @param {string[][]} rows  data rows (plain strings)
 * @returns {string}
 */
export function buildPrintHtml(title, meta, headers, rows) {
  const thCells = headers.map(h => `<th>${escapeHtml(h)}</th>`).join('');
  const trRows = (Array.isArray(rows) ? rows : []).map((row) => {
    const tds = (Array.isArray(row) ? row : []).map(v => `<td>${escapeHtml(v)}</td>`).join('');
    return `<tr>${tds}</tr>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${escapeHtml(title)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #222; margin: 0; padding: 16px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  p.meta { font-size: 11px; color: #666; margin: 0 0 12px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f3f4f6; color: #333; text-align: left; padding: 6px 8px; font-size: 11px; white-space: nowrap; }
  td { padding: 5px 8px; font-size: 11px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  tr:last-child td { border-bottom: none; }
  @media print {
    body { padding: 0; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    a { text-decoration: none; color: inherit; }
  }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<p class="meta">${escapeHtml(meta)}</p>
<table>
  <thead><tr>${thCells}</tr></thead>
  <tbody>
${trRows}
  </tbody>
</table>
</body>
</html>`;
}

function escapeHtml(str) {
  return String(str === null || str === undefined ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── exportFilename ────────────────────────────────────────────────────────────

/**
 * Build an export filename for the given queue and date range.
 * e.g. overdue-recall_2026-04-17_to_2026-10-17.csv
 *
 * @param {string} queue  'overdueRecall' | 'unscheduledTreatment'
 * @param {{ from: string, to: string }} range  YYYY-MM-DD strings
 * @param {string} ext  'csv' or 'html'
 * @returns {string}
 */
export function exportFilename(queue, range, ext) {
  const slug = (queue || '').replace(/([A-Z])/g, c => '-' + c.toLowerCase());
  const from = (range && range.from) ? String(range.from) : '';
  const to = (range && range.to) ? String(range.to) : '';
  const parts = [slug, from && to ? `${from}_to_${to}` : (from || to)].filter(Boolean);
  return parts.join('_') + '.' + (ext || 'csv');
}
